import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import * as z from "zod"
import { TextField } from "~/fields/text-field"
import { TextareaField } from "~/fields/textarea-field"
import { SelectField } from "~/fields/select-field"
import { LettermemeUsersField } from "~/lettermemes/lettermeme-users-field"
import { Button } from "~/ui/button"
import { Form } from "~/ui/form"
import { MutationResult } from "@apollo/client"
import { useFormErrors } from "~/common/use-form-errors"
import { CopyButton } from "~/ui/copy-button"

const emailCadenceOptions = [
  { value: "DAILY" as const, label: "Daily" },
  { value: "WEEKLY" as const, label: "Weekly" },
]

const updateCadenceOptions = [
  { value: "HOURLY" as const, label: "Hourly" },
  { value: "DAILY" as const, label: "Daily" },
  { value: "WEEKLY" as const, label: "Weekly" },
]

const privacyOptions = [
  {
    value: "PUBLIC" as const,
    label: "Public: show on lettermeme index and allow users to subscribe",
  },
  {
    value: "HIDDEN" as const,
    label: "Hidden: hide from lettermeme index, allow users to subscribe ",
  },
  {
    value: "PRIVATE" as const,
    label: "Private: hide from lettermeme index, no subscription available",
  },
]

const generateCartoonsOptions = [
  { value: "true" as const, label: "Yes" },
  { value: "false" as const, label: "No" },
]

const lettermemeFormSchema = z.object({
  name: z.string().min(1, { message: "Name is required" }),
  urlPath: z.string().min(1, { message: "URL path is required" }),
  shareableEmailIn: z
    .string()
    .min(1, { message: "Shareable email is required" })
    .regex(/^[a-zA-Z0-9.]+$/, {
      message: "Can only contain letters, numbers, and periods",
    }),
  secretEmailIn: z.string().optional(),
  publicDescription: z.string().optional(),
  emailHeaderText: z.string().optional(),
  summaryTonePrompt: z.string().optional(),
  cartoonStylePrompt: z.string().optional(),
  emailCadence: z.enum(["DAILY", "WEEKLY"]),
  updateCadence: z.enum(["HOURLY", "DAILY", "WEEKLY"]),
  userEmails: z.array(z.string().email()),
  privacy: z.enum(["PUBLIC", "HIDDEN", "PRIVATE"]),
  generateCartoons: z.enum(["true", "false"]),
})

export type LettermemeFormValues = z.infer<typeof lettermemeFormSchema>

interface LettermemeFormProps {
  defaultValues?: Partial<LettermemeFormValues>
  onSubmit: (values: LettermemeFormValues) => void
  isSubmitting?: boolean
  submitLabel?: string
  onCancel?: () => void
  mutationResult: MutationResult
  ownerEmails?: string[]
}

export const LettermemeForm: React.FC<LettermemeFormProps> = ({
  defaultValues,
  onSubmit,
  isSubmitting = false,
  submitLabel = "Save Changes",
  onCancel,
  mutationResult,
  ownerEmails,
}) => {
  const form = useForm<LettermemeFormValues>({
    resolver: zodResolver(lettermemeFormSchema),
    defaultValues: {
      name: defaultValues?.name || "",
      urlPath: defaultValues?.urlPath || "",
      shareableEmailIn: defaultValues?.shareableEmailIn || "",
      secretEmailIn: defaultValues?.secretEmailIn || "",
      publicDescription: defaultValues?.publicDescription || "",
      emailHeaderText: defaultValues?.emailHeaderText || "",
      summaryTonePrompt: defaultValues?.summaryTonePrompt || "",
      cartoonStylePrompt: defaultValues?.cartoonStylePrompt || "",
      emailCadence: defaultValues?.emailCadence || "WEEKLY",
      updateCadence: defaultValues?.updateCadence || "DAILY",
      userEmails: defaultValues?.userEmails || [],
      privacy: defaultValues?.privacy || "HIDDEN",
      generateCartoons:
        defaultValues?.generateCartoons !== undefined
          ? (String(defaultValues.generateCartoons) as "true" | "false")
          : "true",
    },
  })

  useFormErrors(form.setError, mutationResult)

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <TextField control={form.control} name="name" label="Name" required />

        <TextField
          control={form.control}
          name="urlPath"
          label="URL Path (lettermeme.com/)"
          required
        />

        <div className="flex flex-col gap-6 md:flex-row">
          <div className="flex-1">
            <TextField
              control={form.control}
              name="shareableEmailIn"
              label="Shareable Email In (@lettermeme.com)"
              required
            />
          </div>
          {defaultValues?.secretEmailIn && (
            <div className="relative flex-1">
              <TextField
                control={form.control}
                name="secretEmailIn"
                label="Secret Email In (@lettermeme.com)"
              />
              <CopyButton
                text={defaultValues.secretEmailIn}
                description="Secret email address"
                showCopyText
                className="absolute bottom-2.5 right-2.5"
              />
            </div>
          )}
        </div>

        <TextareaField control={form.control} name="publicDescription" label="Public Description" />

        <TextareaField control={form.control} name="emailHeaderText" label="Email Header Text" />

        <div className="flex flex-col gap-6 md:flex-row">
          <div className="flex-1">
            <SelectField
              control={form.control}
              name="emailCadence"
              label="Email Cadence"
              options={emailCadenceOptions}
              required
            />
          </div>
          <div className="flex-1">
            <SelectField
              control={form.control}
              name="updateCadence"
              label="Update Cadence"
              options={updateCadenceOptions}
              required
            />
          </div>
        </div>

        <SelectField
          control={form.control}
          name="privacy"
          label="Privacy Setting"
          options={privacyOptions}
          required
        />

        <LettermemeUsersField
          control={form.control}
          label="Contributors"
          name="userEmails"
          ownerEmails={ownerEmails}
        />

        <TextareaField
          control={form.control}
          name="summaryTonePrompt"
          label="Custom Summary Tone/Prompt"
          description="Control the tone of your Lettermeme."
        />

        <SelectField
          control={form.control}
          name="generateCartoons"
          label="Generate Cartoons"
          options={generateCartoonsOptions}
          required
        />

        <TextareaField
          control={form.control}
          name="cartoonStylePrompt"
          label="Cartoon Style Prompt"
          description="Override visual styling for your Lettermeme."
        />

        <div className="flex gap-2">
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Saving..." : submitLabel}
          </Button>
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </Button>
          )}
        </div>
      </form>
    </Form>
  )
}
